import dayjs from 'dayjs';

import type { ApiError } from '@/api/config';
import { UserRoles } from '@/store/useUser';
import { Assignment, INDEPENDENT_TYPE_LABELS } from '@/types/vehicle-attendance';

/** Yoklama/uyarı ekranlarını görebilen gruplar (sözleşme §0: diğer gruplar tüm uçlarda 403) */
export const ATTENDANCE_GROUPS: UserRoles[] = [
  UserRoles.ADMIN,
  UserRoles.SUPERADMIN,
  UserRoles.OPERATION,
  UserRoles.XDOCK,
  UserRoles.XDOCK_ADMIN
];
/** Bölge Araç Atamaları ekranı (XDOCK_ADMIN yalnız talep arayüzü) */
export const ASSIGNMENT_PAGE_GROUPS: UserRoles[] = [UserRoles.ADMIN, UserRoles.SUPERADMIN, UserRoles.XDOCK_ADMIN];
/** Bölge Alanları ekranı */
export const AREA_PAGE_GROUPS: UserRoles[] = [UserRoles.ADMIN, UserRoles.SUPERADMIN];

export const hasAnyGroup = (groups: UserRoles[] | undefined | null, allowed: UserRoles[]) => !!groups?.some((g) => allowed.includes(g));

/** Geçmiş okuma sınırı (plan §11.19: saklama = okuma = 400 gün) */
export const ATTENDANCE_MAX_BACK_DAYS = 400;

/** TR takvim günü "YYYY-MM-DD" (tarayıcı saat diliminden bağımsız) */
export const trToday = (): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(
      new Date()
    );
  } catch {
    return dayjs().format('YYYY-MM-DD');
  }
};

/** null/undefined → "—" (sözleşme: bilinmeyen değer asla 0 gösterilmez) */
export const fmtNum = (v: number | null | undefined, digits = 0): string =>
  v === null || v === undefined || !Number.isFinite(v)
    ? '—'
    : v.toLocaleString('tr-TR', { maximumFractionDigits: digits, minimumFractionDigits: 0 });

export const fmtKm = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${fmtNum(v, 1)} km`);

/** Saniye → "3 sa 12 dk" (null → "—") */
export const fmtDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds / 60));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} dk`;
  return m ? `${h} sa ${m} dk` : `${h} sa`;
};

/** ISO zaman → "12 dk önce" (null → "—") */
export const fmtRelative = (iso: string | null | undefined, now: number = Date.now()): string => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diffMin = Math.round((now - t) / 60000);
  if (diffMin < 1) return 'az önce';
  if (diffMin < 60) return `${diffMin} dk önce`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h} sa önce`;
  const d = Math.floor(h / 24);
  return `${d} gün önce`;
};

export const fmtDateTime = (iso: string | null | undefined, format = 'DD.MM.YYYY HH:mm') => (iso ? dayjs(iso).format(format) : '—');

export const fmtDay = (day: string | null | undefined) => (day ? dayjs(day, 'YYYY-MM-DD').format('DD.MM.YYYY') : '—');

export const apiErrorMessage = (e: unknown, fallback = 'İşlem başarısız'): string => {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
};

export const apiErrorStatus = (e: unknown): number | undefined => (e as ApiError | undefined)?.status;

export const isConflictError = (e: unknown) => apiErrorStatus(e) === 409;

/** Atama → kısa metin ("ADANA" / "Bağımsız (Ara transfer)" / "Atanmamış") */
export const assignmentText = (a: Assignment | null | undefined) => {
  if (!a) return 'Atanmamış';
  if (a.mode === 'REGION') return a.xdockName ?? `Bölge #${a.xdockId ?? '—'}`;
  return `Bağımsız (${a.independentType ? INDEPENDENT_TYPE_LABELS[a.independentType] : '—'})`;
};
